import { useState, useRef, useEffect, useCallback } from 'react';
import { Send, Loader2, CheckCircle2, XCircle, SquarePen } from 'lucide-react';
import { usePostHelpChat } from '@edison/api-client-react';
import { getDiagnosticSnapshot } from '@/lib/diagnostics.js';
import { useAuthStore } from '@/store/auth-store.js';

interface TicketDraft {
  title: string;
  description: string;
  type: 'bug' | 'feature';
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

interface AIChatColumnProps {
  appContext: string;
  onGuideCited: (id: string) => void;
  onMessageSent?: () => void;
  /** When true, hides the message thread and only shows the compose bar */
  compact?: boolean;
}

function storageKey(userId: string, tenantId: string) {
  return `help:chat:messages:${tenantId}:${userId}`;
}

export function AIChatColumn({ appContext, onGuideCited, onMessageSent, compact = false }: AIChatColumnProps) {
  const { user, isLoading: authLoading } = useAuthStore();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isPending, setIsPending] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<TicketDraft | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const loadedForRef = useRef<string | null>(null);
  const { mutateAsync: postChat } = usePostHelpChat();

  // Load from per-user storage once auth is resolved
  useEffect(() => {
    if (authLoading || !user) return;
    const key = storageKey(user.id, user.tenantId);
    if (loadedForRef.current === key) return; // already loaded for this user
    loadedForRef.current = key;
    try {
      const stored = localStorage.getItem(key);
      if (stored) setMessages(JSON.parse(stored) as ChatMessage[]);
    } catch { /* ignore */ }
  }, [user, authLoading]);

  // Persist on every change — skip if auth not ready
  useEffect(() => {
    if (!user) return;
    try {
      const toStore = messages.slice(-50);
      localStorage.setItem(storageKey(user.id, user.tenantId), JSON.stringify(toStore));
    } catch { /* ignore */ }
  }, [messages, user]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingDraft]);

  const sendMessage = useCallback(async (text: string, confirmDraft?: TicketDraft) => {
    const userMsg: ChatMessage = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setIsPending(true);
    onMessageSent?.();

    if (confirmDraft) {
      setPendingDraft(null);
    }

    try {
      const body: Parameters<typeof postChat>[0]['data'] = {
        messages: nextMessages,
        pageContext: appContext,
      };

      if (confirmDraft) {
        body.confirmSubmit = true;
        body.draft = confirmDraft;
        body.diagnostics = getDiagnosticSnapshot();
      }

      const res = await postChat({ data: body });
      const data = res.data;

      if (!data) return;

      if (data.type === 'answer' && data.citedGuideId) {
        onGuideCited(data.citedGuideId);
      }

      if (data.type === 'confirm_submit') {
        setPendingDraft({
          title: data.draft.title,
          description: data.draft.description,
          type: data.draft.type as 'bug' | 'feature',
        });
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: data.message },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: "I couldn't reach the assistant right now. You can browse the guides or submit a ticket directly.",
        },
      ]);
    } finally {
      setIsPending(false);
    }
  }, [messages, appContext, postChat, onGuideCited]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      const trimmed = input.trim();
      if (trimmed && !isPending) void sendMessage(trimmed);
    }
  }

  function handleSendClick() {
    const trimmed = input.trim();
    if (trimmed && !isPending) void sendMessage(trimmed);
  }

  function handleConfirm() {
    if (pendingDraft && !isPending) void sendMessage('Confirmed.', pendingDraft);
  }

  function handleEdit() {
    setPendingDraft(null);
  }

  function handleNewConversation() {
    if (isPending) return;
    setMessages([]);
    setPendingDraft(null);
    setInput('');
    if (user) {
      try { localStorage.removeItem(storageKey(user.id, user.tenantId)); } catch { /* ignore */ }
    }
  }

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Message thread — hidden in compact mode */}
      <div className={`overflow-y-auto p-4 space-y-4 ${compact ? 'hidden' : 'flex-1'}`}>
        {messages.length === 0 && !isPending && (
          <div className="text-sm text-white/40 text-center py-8">
            Ask me anything about Edison — I&apos;ll answer from the guides or help you log a report.
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-white/8 text-primary-foreground/90 rounded-bl-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {/* ConfirmCard */}
        {pendingDraft && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-white/40">
              Ready to submit
            </p>
            <p className="text-sm text-primary-foreground font-medium">{pendingDraft.title}</p>
            <p className="text-sm text-primary-foreground/60 leading-relaxed">{pendingDraft.description}</p>
            <div className="flex gap-2 pt-1">
              <button
                onClick={handleConfirm}
                disabled={isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 text-white text-sm rounded-lg transition-colors"
                aria-label="Confirm submission"
              >
                <CheckCircle2 className="h-4 w-4" />
                Confirm
              </button>
              <button
                onClick={handleEdit}
                disabled={isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-white/8 hover:bg-white/12 disabled:opacity-40 text-primary-foreground/70 text-sm rounded-lg transition-colors"
              >
                <XCircle className="h-4 w-4" />
                Edit
              </button>
            </div>
          </div>
        )}

        {isPending && (
          <div className="flex justify-start">
            <div className="bg-white/8 rounded-xl rounded-bl-sm px-3.5 py-2.5">
              <Loader2 className="h-4 w-4 animate-spin text-white/40" />
            </div>
          </div>
        )}
        <div ref={scrollRef} />
      </div>

      {/* Compose */}
      <div className="p-3 border-t border-white/8 shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question or describe an issue..."
            rows={1}
            className="flex-1 bg-white/5 rounded-lg px-3 py-2 text-sm text-primary-foreground placeholder:text-white/30 outline-none focus:ring-1 focus:ring-white/20 resize-none"
          />
          {!compact && messages.length > 0 && (
            <button
              onClick={handleNewConversation}
              disabled={isPending}
              className="p-2 bg-white/8 hover:bg-white/14 disabled:opacity-40 disabled:cursor-not-allowed text-white/60 hover:text-white/90 rounded-lg transition-colors shrink-0"
              aria-label="New conversation"
              title="New conversation"
            >
              <SquarePen className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={handleSendClick}
            disabled={!input.trim() || isPending}
            className="p-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors shrink-0"
            aria-label="Send message"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
